/**
 * DELETE A TEMPLATE — WHICH MEANS ARCHIVE IT, UNLESS NOTHING POINTS AT IT.
 *
 * 🔴 HIS WORD, 2026-09-19 (item 5 of the four re-raised to him): Delete on W17d's overview window
 * means ARCHIVE. This module is the decision; `WorkoutOverviewWindow` is the surface.
 *
 * WHY THERE ARE TWO BRANCHES AT ALL. A coach who made a template by accident an hour ago wants it
 * GONE, and telling them "archived" about a row nothing has ever touched is a tidy word for a list
 * that is still cluttered. A coach deleting a template they have been assigning for six weeks
 * means something entirely different, and the database means something else again.
 *
 * 🔴 MEASURED LIVE 2026-09-19 FROM `pg_constraint`, AND THE SECOND HALF IS WHY THE TEST IS NOT
 * "ZERO COPIES":
 *   • `coach_workouts_root_workout_id_fkey`   ON DELETE **SET NULL**
 *   • `coach_assignments_root_workout_id_fkey` ON DELETE **SET NULL**
 *   • `coach_assignments_workout_id_fkey`      ON DELETE **CASCADE**
 * Deleting a template with copies re-keys every copy and every assignment (both key on
 * `coalesce(root_workout_id, workout_id)`, so a six-week-old template would read "Never
 * assigned" and two workouts could collide on the one-source-per-day index).
 * **AND — measured the same hour — `Wrist series` has ZERO COPIES AND FOURTEEN ASSIGNMENTS
 * POINTING STRAIGHT AT IT** (`workout_id = <the template>`, `root_workout_id` null): task rows
 * are assigned in place and never copied, and that foreign key is CASCADE. A "zero copies" rule
 * would have deleted fourteen days of his own history. Of his 15 templates, 9 have zero copies
 * and only 3 have zero copies AND zero assignments.
 *
 * ⚠️ SO THE MEASUREMENT IS TAKEN AT CLICK TIME, NEVER INFERRED FROM THE LIST. The library's rows
 * carry a `lastUsedISO` derived from assignments, and it would be tempting to read "never
 * assigned" as "safe to delete" — it is not: `lastUsedISO` is null for a template whose only
 * assignments are in the FUTURE, and it says nothing at all about copies. Two counts, read when
 * the coach presses the button, against the live database.
 */

/** What points at this template right now. Both counts come from one read at click time. */
export type WorkoutUsage = {
  /** Client-scoped copies (`coach_workouts.root_workout_id = <this>`). */
  copies: number;
  /** Assignments naming it either directly or through a copy's root. */
  assignments: number;
};

export type RemoveOutcome =
  /** The row is gone. Only ever reached when `isDetached` was true. */
  | { kind: "deleted" }
  /** The row is archived: out of the library, every copy and assignment untouched. */
  | { kind: "archived" }
  /** Nothing happened, and the library is unchanged. */
  | { kind: "failed"; message: string };

/**
 * A real row delete is safe ONLY when nothing points at this template — no client copy AND no
 * assignment. Both halves are load-bearing and each was measured to be insufficient alone.
 */
export function isDetached(usage: WorkoutUsage): boolean {
  return usage.copies === 0 && usage.assignments === 0;
}

/**
 * THE SENTENCE THE COACH READS BEFORE ANYTHING HAPPENS — and it says what will actually happen,
 * which is the whole reason the counts are measured first.
 *
 * ⚠️ NOT "ARE YOU SURE". A confirm that only asks for a second press transfers no information; a
 * coach learns to press through it. These two name the consequence and they are DIFFERENT
 * consequences, so the confirm is where the branch becomes visible rather than a surprise
 * afterwards.
 */
export function removeSentence(name: string, usage: WorkoutUsage): string {
  if (isDetached(usage)) {
    return `Delete “${name}”? Nothing is assigned to it and no client has a copy of it, `
      + `so it is removed for good.`;
  }
  return `Archive “${name}”? It is removed from your library; days already assigned keep `
    + `their copies.`;
}

/**
 * The word on the button that commits it — again, the branch said out loud rather than one verb
 * covering two different acts.
 */
export function removeVerb(usage: WorkoutUsage): "Delete" | "Archive" {
  return isDetached(usage) ? "Delete" : "Archive";
}

/**
 * What the library says after it is done. `archived` is deliberately not silent: a row that
 * vanishes with no word reads as a delete, which is exactly the thing this design refuses.
 */
export function removedSentence(name: string, outcome: RemoveOutcome): string | null {
  if (outcome.kind === "deleted") return `“${name}” was deleted.`;
  if (outcome.kind === "archived") {
    return `“${name}” is archived. It is out of your library; assigned days keep their copies.`;
  }
  return null;
}

/** The three calls this needs, injected — so the decision is executed by a test with no network. */
export type RemoveDeps = {
  /** ONE read, at click time, returning both counts. */
  usage: (workoutId: string) => Promise<{ usage: WorkoutUsage | null; error: string | null }>;
  archive: (workoutId: string, atISO: string) => Promise<{ error: string | null }>;
  remove: (workoutId: string) => Promise<{ error: string | null }>;
};

/**
 * ⚠️ THE USAGE IS RE-READ HERE EVEN IF THE CALLER ALREADY HAS IT, AND THAT IS NOT BELT-AND-BRACES.
 * The confirm sentence is written from a reading taken when the coach pressed Delete; the act
 * happens when they press the confirm. Between the two, an assignment can land (the coach's own
 * other tab, the auto-apply writer). A delete decided on a stale count is the one failure here
 * that cannot be undone, so the count that DECIDES is read immediately before acting — and when
 * the two readings disagree the act downgrades to an archive, never up to a delete.
 *
 * ⚠️ AND A FAILURE TO READ IT IS A REFUSAL, NOT A FALLBACK. Every other read in this app falls
 * back to the quieter half; here the quiet half is "assume nothing points at it", which is the
 * destructive one. No count, no act.
 */
export async function removeWorkout(
  deps: RemoveDeps,
  workout: { id: string; name: string },
  now: Date,
): Promise<RemoveOutcome> {
  const read = await deps.usage(workout.id);
  if (read.error !== null || read.usage === null) {
    return {
      kind: "failed",
      message:
        `Could not check what uses “${workout.name}”, so nothing was changed: `
        + `${read.error ?? "the check returned nothing."}`,
    };
  }
  if (isDetached(read.usage)) {
    const gone = await deps.remove(workout.id);
    if (gone.error !== null) {
      return { kind: "failed", message: `Could not delete “${workout.name}”: ${gone.error}` };
    }
    return { kind: "deleted" };
  }
  const done = await deps.archive(workout.id, now.toISOString());
  if (done.error !== null) {
    return { kind: "failed", message: `Could not archive “${workout.name}”: ${done.error}` };
  }
  return { kind: "archived" };
}
